/**
 * Arc Geometry Utilities
 *
 * Core geometric calculations for the rowing arc visualization.
 * These functions handle angle conversions, arc segment calculations,
 * and blade path geometry for the rowing stroke arc diagram.
 */

import * as d3 from 'd3';
import { ARC_COLORS } from '../features/dashboards/arcConfig';

/**
 * Converts blade angle (alpha) in degrees to polar angle (theta) in radians.
 * Alpha is measured from the centerline, positive toward bow, negative toward stern.
 * Theta is the standard polar coordinate angle for D3 arc rendering.
 *
 * @param alphaDeg - Blade angle in degrees (positive = bow, negative = stern)
 * @returns Theta angle in radians for D3 arc generation
 *
 * @example
 * alphaToTheta(-60) // Catch position (stern side)
 * alphaToTheta(30)  // Finish position (bow side)
 */
export const alphaToTheta = (alphaDeg: number): number => (-alphaDeg * Math.PI) / 180;

/**
 * Formats an angle in degrees with sign prefix for display.
 *
 * @param alphaDeg - Angle in degrees
 * @returns Formatted string (e.g., "+30°", "-60°", "0°")
 */
export const formatAngle = (alphaDeg: number): string => `${Math.round(Math.abs(alphaDeg))}°`;

/**
 * Formats an angle range for display.
 *
 * @param range - Tuple of [start, end] angles in degrees
 * @returns Formatted string (e.g., "-60° → +30°")
 */
export const formatAngleRange = (range: [number, number]): string =>
  `${formatAngle(range[0])} → ${formatAngle(range[1])}`;

/**
 * Applies opacity to a hex or named color string.
 *
 * @param hex - Color string (hex or named CSS color)
 * @param opacity - Opacity value (0-1)
 * @returns Color string with opacity applied
 */
export const applyOpacity = (hex: string, opacity: number): string => {
  const parsed = d3.color(hex);
  if (!parsed) return hex;
  const copy = parsed.copy();
  copy.opacity = opacity;
  return copy.toString();
};

/**
 * Sanitizes a string for use as an HTML/SVG ID.
 * Removes all characters except alphanumeric, hyphens, and underscores.
 *
 * @param rawId - Raw string to sanitize
 * @returns Sanitized ID string safe for HTML/SVG
 */
export const sanitizeId = (rawId: string): string => rawId.replace(/[^a-zA-Z0-9-_]/g, '');

/**
 * Builds an SVG path string for an oar blade.
 * The blade is rendered as a quadrilateral perpendicular to the oar shaft.
 *
 * @param bladeRootX - X coordinate where blade connects to shaft
 * @param bladeRootY - Y coordinate where blade connects to shaft
 * @param bladeTipX - X coordinate of blade tip (in water)
 * @param bladeTipY - Y coordinate of blade tip
 * @param unitPerpX - Unit vector perpendicular to shaft (X component)
 * @param unitPerpY - Unit vector perpendicular to shaft (Y component)
 * @param bladeWidth - Width of blade in SVG units
 * @returns SVG path string for blade polygon
 */
export const buildBladePath = (
  bladeRootX: number,
  bladeRootY: number,
  bladeTipX: number,
  bladeTipY: number,
  unitPerpX: number,
  unitPerpY: number,
  bladeWidth: number
): string => {
  const halfWidth = bladeWidth / 2;

  const p1x = bladeRootX + unitPerpX * halfWidth;
  const p1y = bladeRootY + unitPerpY * halfWidth;
  const p2x = bladeTipX + unitPerpX * halfWidth;
  const p2y = bladeTipY + unitPerpY * halfWidth;
  const p3x = bladeTipX - unitPerpX * halfWidth;
  const p3y = bladeTipY - unitPerpY * halfWidth;
  const p4x = bladeRootX - unitPerpX * halfWidth;
  const p4y = bladeRootY - unitPerpY * halfWidth;

  return `M ${p1x},${p1y} L ${p2x},${p2y} L ${p3x},${p3y} L ${p4x},${p4y} Z`;
};

/**
 * Segment geometry defining an arc section with angular extent.
 */
export interface SegmentGeometry {
  startAlpha: number;
  endAlpha: number;
  color: string;
  name: string;
}

/**
 * Complete stroke geometry including segments and metrics.
 */
export interface StrokeGeometry {
  segments: {
    catchSlip: SegmentGeometry;
    effectiveLength: SegmentGeometry;
    finishSlip: SegmentGeometry;
  };
  metrics: {
    totalArc: number;
    effectiveLength: number;
    catchSlipRange: [number, number];
    effectiveRange: [number, number];
    finishSlipRange: [number, number];
  };
  extents: {
    catchAlpha: number;
    finishAlpha: number;
  };
}

/**
 * Sample data structure for a single rowing stroke.
 */
export interface StrokeSample {
  strokeId: string;
  label: string;
  catchAngleDeg: number;
  finishAngleDeg: number;
  catchSlipDeg: number;
  finishSlipDeg: number;
  driveStartMs: number | null;
  driveTimeMs: number | null;
  recoveryTimeMs: number | null;
  dataSource: string;
  confidence: number | null;
  strokeIndex: number;
  rowerPosition: number | null;
}

/**
 * Calculates complete stroke geometry from a stroke sample.
 * Computes catch slip, effective length, and finish slip segments
 * with their angular ranges and metrics.
 *
 * @param sample - Stroke data containing angles and slip measurements
 * @returns Complete stroke geometry with segments and metrics
 *
 * @example
 * const geometry = calculateStrokeGeometry({
 *   catchAngleDeg: 60,
 *   finishAngleDeg: 30,
 *   catchSlipDeg: 8,
 *   finishSlipDeg: 5,
 *   // ... other fields
 * });
 */
export const calculateStrokeGeometry = (sample: StrokeSample): StrokeGeometry => {
  const catchAlpha = -sample.catchAngleDeg;
  const catchSlipEndAlpha = catchAlpha + sample.catchSlipDeg;
  const finishAlpha = sample.finishAngleDeg;
  const finishSlipStartAlpha = finishAlpha - sample.finishSlipDeg;

  const totalArc = sample.catchAngleDeg + sample.finishAngleDeg;
  const effectiveLength = totalArc - (sample.catchSlipDeg + sample.finishSlipDeg);

  return {
    segments: {
      catchSlip: {
        startAlpha: catchAlpha,
        endAlpha: catchSlipEndAlpha,
        color: ARC_COLORS.catchSlip,
        name: 'Catch Gap',
      },
      effectiveLength: {
        startAlpha: catchSlipEndAlpha,
        endAlpha: finishSlipStartAlpha,
        color: ARC_COLORS.effectiveLength,
        name: 'Effective Length',
      },
      finishSlip: {
        startAlpha: finishSlipStartAlpha,
        endAlpha: finishAlpha,
        color: ARC_COLORS.finishSlip,
        name: 'Finish Slip',
      },
    },
    metrics: {
      totalArc,
      effectiveLength,
      catchSlipRange: [catchAlpha, catchSlipEndAlpha],
      effectiveRange: [catchSlipEndAlpha, finishSlipStartAlpha],
      finishSlipRange: [finishSlipStartAlpha, finishAlpha],
    },
    extents: {
      catchAlpha,
      finishAlpha,
    },
  };
};
